import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { withTheme } from "../theming";
import { userPool } from "../awsConfig";
import { useResponsive } from "../contexts/ResponsiveContext";
import { clearAuthState } from "../utils/authUtils";

const Header = ({ isAuthenticated, setIsAuthenticated, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startupName, setStartupName] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { isCompactNav, isPortrait, isPhoneLandscape } = useResponsive();
  // Hamburger: any compact viewport (<1100px), portrait orientation, or short landscape phones.
  // The desktop menu has 12 items at 1.2rem and needs ~1400px to lay out cleanly.
  const showHamburger = isCompactNav || isPortrait || isPhoneLandscape;

  // Check if we're on a public/auth page where menu items should be hidden
  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/confirm-signup";

  // Only show menu items if authenticated AND not on auth pages
  const shouldShowMenuItems = isAuthenticated && !isAuthPage;

  useEffect(() => {
    // Get startup name from localStorage
    const storedStartupName = localStorage.getItem("STARTUP");
    if (storedStartupName) {
      setStartupName(storedStartupName);
    }
  }, []);

  const handleLogout = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }

    // Close the menu first
    setIsOpen(false);

    // Clear all authentication state (this also closes all modals)
    clearAuthState();
    setIsAuthenticated(false);

    // Force navigation to login page (replace prevents back button)
    window.location.replace("/login");
  };

  // Function to handle menu item click
  const handleMenuItemClick = () => {
    setIsOpen(false);
  };

  // Menu links - Content Creation Tools
  // Based on Agent outputs:
  // Agent 1: Content Analysis (key moments, quotes, frames, transcript)
  // Agent 2: Video Segments (segmentation and recommendations)
  // Agent 3: Text Content (blog posts, Twitter threads, descriptions)
  // Agent 4: Visual Assets (quote cards, carousels, thumbnails, story covers)
  // Mirrors menuLinks exactly — same names, same order.
  // Unauth users land on the feature marketing page; items without one go to /get-started.
  const publicMenuLinks = [
    { name: "Who It's For", path: "/who-its-for" },
    { name: "About", path: "/about" },
    { name: "Built to Last", path: "/built-to-last" },
    { name: "Submit Video", path: "/features/submit-video" },
    { name: "Submit Doc", path: "/features/submit-doc" },
    { name: "Video Status", path: "/features/video-status" },
    { name: "Video History", path: "/features/video-history" },
    { name: "Assets Studio", path: "/features/assets-studio" },
    // { name: "Blogs", path: "/features/blogs" }, // not in use right now — likely returning
    { name: "Brand Cards", path: "/features/brand-cards" },
    { name: "Video Editor", path: "/features/video-editor" },
    // { name: "Social Planner", path: "/features/social-planner" },     // moved to x20-social
    // { name: "Social Publisher", path: "/features/social-publisher" }, // moved to x20-social
    // { name: "Pub Calendar", path: "/features/publication-calendar" }, // moved to x20-social
    // { name: "Publish Rules", path: "/features/publish-rules" },       // moved to x20-social
    // { name: "Review Queue", path: "/features/review-queue" },         // moved to x20-social
    // { name: "Social Sites", path: "/features/social-sites" },         // moved to x20-social
    // { name: "Ad Agency", path: "/features/ad-agency" },  // moved to x20-business-dev
    // { name: "Reporter Agent", path: "/features/reporter-agent" },  // moved to x20-research
    // { name: "Research Deck", path: "/features/research-deck" },    // moved to x20-research
    { name: "Your Brand", path: "/features/your-brand" },
    { name: "Help", path: "/features/help" },
  ];

  const menuLinks = [
    { name: "Submit Video", path: "/video-submission" },
    { name: "Submit Doc", path: "/document-submission" },
    // { name: "Queue Video Upload", path: "/video-upload-queue" },
    { name: "Video Status", path: "/video-processing-status" },
    // { name: "Cost Reports", path: "/cost-reports" }, // not in use right now — likely returning
    { name: "Video History", path: "/content-feed" },
    // { name: "Topics & Key Moments", path: "/content-analysis" }, // Agent 1
    // { name: "Quotes & Thumbnails", path: "/visual-assets" }, // Agent 4
    // { name: "Generated Assets", path: "/approved-assets" },
    { name: "Assets Studio", path: "/assets-studio-v2" },
    // { name: "Blogs", path: "/blogs" }, // not in use right now — likely returning
    { name: "Brand Cards", path: "/brand-cards" },
    { name: "Video Editor", path: "/video-editor" },
    // { name: "Social Planner", path: "/social-planner" },     // moved to x20-social
    // { name: "Social Publisher", path: "/social-publisher" }, // moved to x20-social
    // { name: "Pub Calendar", path: "/publication-calendar" }, // moved to x20-social
    // { name: "Publish Rules", path: "/auto-publish-rules" },  // moved to x20-social
    // { name: "Review Queue", path: "/review-queue" },         // moved to x20-social
    // { name: "Remotion Editor", path: "/piper-video-studio" },
    // { name: "Piper Editor", path: "/piper-creative-video-editor" },
    // { name: "Video Clips", path: "/video-segments" }, // Agent 2
    // { name: "Blogs & Newsletters", path: "/text-content" }, // Agent 3
    // { name: "Content Composer", path: "/content-composer" },
    // { name: "Agent Designer", path: "/agent-config" },
    // { name: "Social Sites", path: "/agentteam/social-sites" }, // moved to x20-social
    // { name: "Ad Agency", path: "/agentteam/ad-agency" },  // moved to x20-business-dev
    // { name: "Reporter Agent", path: "/reporter-agent" },              // moved to x20-research
    // { name: "Research Deck", path: "/research-deck" },                // moved to x20-research
    // { name: "Architecture Diagrams", path: "/architecture-diagrams" }, // moved to x20-research
    // { name: "Your Profile", path: "/agentteam/creator-profile" },
    // { name: "Your Pillars", path: "/agentteam/content-pillars" },
    // { name: "Your Competitors", path: "/agentteam/competitor-insights" },
    { name: "Your Brand", path: "/brand-management-studio" },
    { name: "About", path: "/about" },
    { name: "Built to Last", path: "/built-to-last" },
    { name: "Help", path: "/help" },
  ];

  return (
    <header
      style={{
        backgroundColor: theme.backgroundColor || "#333",
        color: theme.textColor || "#FFF",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
      className="p-4"
    >
      <div className="mx-auto flex justify-between items-center pr-10 pl-5">
        {/* Logo */}
        <div className="flex-shrink-0 mr-8">
          <NavLink
            to="/"
            className={({ isActive }) => {
              const sizeCls = showHamburger ? "text-[135%]" : "text-[170%]";
              return isActive
                ? `font-bold ${sizeCls} hover:opacity-90 focus-visible:opacity-90`
                : `${sizeCls} font-bold hover:opacity-90 focus:opacity-90`;
            }}
            style={{ color: theme.accentColor || "#FFA72A" }}
          >
            X20 Creative
          </NavLink>
        </div>

        {/* Hamburger Menu Icon */}
        <div className={showHamburger ? "block" : "hidden"}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="focus:outline-none p-2 rounded-md"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-nav"
            style={{
              backgroundColor: theme.backgroundColor || "#333",
              color: theme.accentColor || "#FFF",
            }}
          >
            <svg
              className="w-7 h-7"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              {isOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Desktop Menu */}
        <nav
          className={showHamburger ? "hidden" : "flex space-x-4 items-center"}
        >
          {shouldShowMenuItems ? (
            <>
              {menuLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    isActive
                      ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                      : "text-[120%] hover:opacity-90 focus:opacity-90"
                  }
                  style={{
                    color: theme.accentColor || "#FFA72A",
                  }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="#"
                onClick={handleLogout}
                className={({ isActive }) =>
                  isActive
                    ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                    : "text-[120%] hover:opacity-90 focus:opacity-90"
                }
                style={{
                  color: theme.accentColor || "#FFA72A",
                }}
              >
                Logout
              </NavLink>
            </>
          ) : (
            <>
              {publicMenuLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    isActive
                      ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                      : "text-[120%] hover:opacity-90 focus:opacity-90"
                  }
                  style={{ color: theme.accentColor || "#FFA72A" }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="/login"
                className={({ isActive }) =>
                  isActive
                    ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                    : "text-[120%] hover:opacity-90 focus:opacity-90"
                }
                style={{ color: theme.accentColor || "#FFA72A" }}
              >
                Login
              </NavLink>
              <NavLink
                to="/get-started"
                style={{
                  backgroundColor: theme.accentColor || "#FFA72A",
                  color: theme.buttonTextColor || "#fff",
                  borderRadius: "50px",
                  padding: "6px 20px",
                  fontWeight: 600,
                  fontSize: "1rem",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                  transition: "opacity 0.2s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                Start Free
              </NavLink>
            </>
          )}
        </nav>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <nav
          id="mobile-nav"
          className={`${showHamburger ? "block" : "hidden"} mt-4 max-h-[calc(100vh-80px)] overflow-y-auto`}
          style={{ borderTop: `1px solid ${theme.accentColor || "#FFA72A"}22` }}
        >
          {shouldShowMenuItems ? (
            <>
              {menuLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  onClick={handleMenuItemClick}
                  className={({ isActive }) =>
                    `block py-3 px-4 text-[120%] border-b ${isActive ? "font-bold" : ""}`
                  }
                  style={{
                    color: theme.accentColor || "#FFA72A",
                    borderColor: `${theme.accentColor || "#FFA72A"}1a`,
                  }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="#"
                onClick={handleLogout}
                className="block py-3 px-4 text-[120%]"
                style={{
                  color: theme.accentColor || "#FFA72A",
                }}
              >
                Logout
              </NavLink>
            </>
          ) : (
            <>
              {publicMenuLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  onClick={handleMenuItemClick}
                  className={({ isActive }) =>
                    `block py-3 px-4 text-[120%] border-b ${isActive ? "font-bold" : ""}`
                  }
                  style={{
                    color: theme.accentColor || "#FFA72A",
                    borderColor: `${theme.accentColor || "#FFA72A"}1a`,
                  }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="/login"
                onClick={handleMenuItemClick}
                className={({ isActive }) =>
                  `block py-3 px-4 text-[120%] border-b ${isActive ? "font-bold" : ""}`
                }
                style={{
                  color: theme.accentColor || "#FFA72A",
                  borderColor: `${theme.accentColor || "#FFA72A"}1a`,
                }}
              >
                Login
              </NavLink>
              <NavLink
                to="/get-started"
                onClick={handleMenuItemClick}
                className="block py-3 px-4 text-[120%] font-semibold"
                style={{ color: theme.accentColor || "#FFA72A" }}
              >
                Start Free
              </NavLink>
            </>
          )}
        </nav>
      )}
    </header>
  );
};

export default withTheme(Header);
